import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Shield, FileText, Clock, CheckCircle } from 'lucide-react';

function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capture affiliate ref from URL (?ref=slug) - supports shared links
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
      localStorage.setItem('affiliate_ref', ref);
      localStorage.setItem('affiliate_ref_expiry', expiry.toString());
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header Simple */}
      <header className="border-b border-border bg-white">
        <div className="container-narrow py-3 sm:py-4 flex justify-between items-center px-4 sm:px-0">
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="w-5 sm:w-6 h-5 sm:h-6 text-accent flex-shrink-0" />
            <h1 className="text-base sm:text-xl font-semibold text-primary truncate">Assistance Rédactionnelle</h1>
          </div>
          <Badge variant="outline" className="text-xs flex-shrink-0 ml-2">
            Suisse romande
          </Badge>
        </div>
      </header>

      {/* Hero Section - COPYWRITING ÉMOTIONNEL */}
      <section className="py-12 sm:py-20">
        <div className="container-narrow">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-6 bg-accent text-white animate-pulse" data-testid="urgent-badge">
              ⚠️ URGENT : Vous avez 30 jours maximum pour répondre
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground mb-6 leading-tight" data-testid="hero-headline">
              <span className="text-accent">Indemnités suspendues.</span>
              <br />
              <span className="text-accent">Expulsion imminente.</span>
              <br />
              <span className="text-accent">Licenciement contesté.</span>
              <br />
              <span className="text-primary">Vous avez 30 jours.</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-foreground font-semibold mb-4">
              Un seul courrier mal rédigé = <span className="text-accent font-bold">TOUT PERDU</span>
            </p>
            
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Pendant que vous hésitez, <span className="font-bold text-foreground">le délai court</span>. 
              Pendant que vous cherchez les bons mots, <span className="font-bold text-foreground">vos droits s'évaporent</span>. 
              Un courrier administratif, ce n'est pas juste des phrases.
              <span className="font-bold text-accent block mt-2">C'est votre survie qui est en jeu.</span>
            </p>
            
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg font-semibold mb-4 w-full sm:w-auto"
              onClick={() => navigate('/order')}
              data-testid="cta-commencer"
            >
              🛡️ Protéger mes droits MAINTENANT
            </Button>
            
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Shield className="w-4 h-4 text-success" />
              Courrier basé sur le Code civil suisse · Livraison 24-48h
            </p>

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-900 font-medium">
                <strong>Assistance rédactionnelle uniquement</strong> — Ce service ne constitue pas un conseil juridique.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Separator className="my-12" />

      {/* Situations - COPYWRITING ÉMOTIONNEL */}
      <section className="py-12 bg-accent/5">
        <div className="container-narrow">
          <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-4 text-foreground">
            Dans quelle situation êtes-vous <span className="text-accent">COINCÉ</span> ?
          </h2>
          <p className="text-center text-lg text-muted-foreground mb-12">
            Chacune de ces situations a <span className="font-bold text-foreground">UN DÉLAI LÉGAL</span>. 
            Dépassé = <span className="font-bold text-accent">AUCUN RECOURS POSSIBLE.</span>
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="p-6 border-2 hover:border-accent transition-colors">
              <FileText className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-semibold text-lg mb-2">Chômage refusé ou suspendu</h3>
              <p className="text-sm text-muted-foreground mb-3">
                "La CSR a coupé mes indemnités. Je n'ai pas fourni les justificatifs à temps. 
                Comment je paie mon loyer ce mois ?"
              </p>
              <p className="text-xs text-accent font-bold">⏰ Délai : 30 jours dès réception</p>
            </Card>

            <Card className="p-6 border-2 hover:border-accent transition-colors">
              <FileText className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-semibold text-lg mb-2">Résiliation de bail / Expulsion</h3>
              <p className="text-sm text-muted-foreground mb-3">
                "Mon propriétaire veut me mettre dehors pour un retard de loyer. 
                Je vais perdre mon appartement..."
              </p>
              <p className="text-xs text-accent font-bold">⏰ Délai : 30 jours pour contester</p>
            </Card>

            <Card className="p-6 border-2 hover:border-accent transition-colors">
              <FileText className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-semibold text-lg mb-2">Licenciement abusif</h3>
              <p className="text-sm text-muted-foreground mb-3">
                "Mon employeur m'a viré du jour au lendemain. J'ai une famille à nourrir. 
                C'est injuste mais je ne sais pas comment me défendre."
              </p>
              <p className="text-xs text-accent font-bold">⏰ Délai : 180 jours pour agir</p>
            </Card>

            <Card className="p-6 border-2 hover:border-accent transition-colors">
              <FileText className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-semibold text-lg mb-2">Aide sociale refusée</h3>
              <p className="text-sm text-muted-foreground mb-3">
                "On me dit que je n'ai pas droit à l'aide sociale. Mais je ne peux plus payer mes factures..."
              </p>
              <p className="text-xs text-accent font-bold">⏰ Délai : 30 jours pour recours</p>
            </Card>

            <Card className="p-6 border-2 hover:border-accent transition-colors">
              <FileText className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-semibold text-lg mb-2">Assurance maladie / LAA</h3>
              <p className="text-sm text-muted-foreground mb-3">
                "Mon assurance refuse de payer mes soins. Je suis en arrêt maladie et ils me coupent tout."
              </p>
              <p className="text-xs text-accent font-bold">⏰ Délai : 30 jours pour contester</p>
            </Card>

            <Card className="p-6 border-2 hover:border-accent transition-colors">
              <FileText className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-semibold text-lg mb-2">Décision administrative</h3>
              <p className="text-sm text-muted-foreground mb-3">
                "J'ai reçu une décision officielle que je ne comprends pas. 
                C'est écrit en jargon juridique incompréhensible."
              </p>
              <p className="text-xs text-accent font-bold">⏰ Délai : variable selon canton</p>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="inline-block p-8 border-2 border-accent/30 bg-accent/10">
              <p className="text-2xl font-bold text-foreground mb-3">
                ⚠️ LA RÉALITÉ BRUTALE
              </p>
              <p className="text-lg mb-4">
                <span className="font-bold text-accent">87% des personnes</span> qui ratent ces délais 
                <span className="font-bold block mt-2 text-foreground">PERDENT DÉFINITIVEMENT LEURS DROITS</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Les administrations ne rappellent JAMAIS. Pas de deuxième chance. Pas de pitié.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="my-12" />

      {/* Pain Points - COPYWRITING ÉMOTIONNEL */}
      <section className="py-12">
        <div className="container-narrow">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-12 text-foreground">
              Pourquoi vous êtes <span className="text-accent">PARALYSÉ</span>
            </h2>
            
            <div className="space-y-6">
              <Card className="p-6 border-l-4 border-accent">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <span className="text-2xl">😰</span> La peur de l'erreur fatale
                </h3>
                <p className="text-muted-foreground mb-3">
                  "Si je dis la mauvaise chose, je vais <span className="font-semibold text-foreground">aggraver mon cas</span>. 
                  Si j'utilise les mauvais termes juridiques, ils vont me rejeter immédiatement. 
                  Mais si je ne réponds pas du tout... c'est encore pire."
                </p>
                <p className="text-sm text-accent font-semibold">
                  → Résultat : Vous ne faites RIEN. Et le délai expire.
                </p>
              </Card>
              
              <Card className="p-6 border-l-4 border-accent">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <span className="text-2xl">💸</span> L'avocat hors de prix
                </h3>
                <p className="text-muted-foreground mb-3">
                  "Un avocat me demande <span className="font-semibold text-foreground">CHF 300-500/heure</span>. 
                  Pour un simple courrier, ça fait facilement <span className="font-semibold text-foreground">CHF 1'500-2'500</span>. 
                  Je n'ai justement PAS cet argent, c'est pour ça que je suis dans cette situation !"
                </p>
                <p className="text-sm text-accent font-semibold">
                  → Résultat : Vous abandonnez vos droits faute de moyens.
                </p>
              </Card>
              
              <Card className="p-6 border-l-4 border-accent">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <span className="text-2xl">⏰</span> Le temps qui file
                </h3>
                <p className="text-muted-foreground mb-3">
                  "J'ai déjà perdu <span className="font-semibold text-foreground">3 semaines</span> à chercher comment rédiger ce courrier. 
                  J'ai lu 15 modèles sur internet, tous différents. Je ne sais toujours pas quoi écrire. 
                  <span className="font-semibold text-foreground">Il me reste 7 jours</span>..."
                </p>
                <p className="text-sm text-accent font-semibold">
                  → Résultat : Vous rédigez un brouillon bâclé à la dernière minute. Rejeté.
                </p>
              </Card>
              
              <Card className="p-6 border-l-4 border-accent">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <span className="text-2xl">📄</span> Le jargon incompréhensible
                </h3>
                <p className="text-muted-foreground mb-3">
                  "Ils parlent de <span className="font-semibold text-foreground">'recours en vertu de l'art. 48 LACI'</span>, 
                  de <span className="font-semibold text-foreground">'subsidiarité'</span>, 
                  de <span className="font-semibold text-foreground">'voies de droit'</span>. 
                  Je ne suis pas juriste, moi ! Je veux juste qu'on me paie ce qu'on me doit."
                </p>
                <p className="text-sm text-accent font-semibold">
                  → Résultat : Vous envoyez un courrier "en langage normal". Ignoré.
                </p>
              </Card>
            </div>
            
            <Card className="mt-12 p-8 bg-accent/10 border-2 border-accent text-center">
              <p className="text-2xl font-bold text-foreground mb-4">
                ⚠️ STOP. Vous n'avez plus le temps de tergiverser.
              </p>
              <p className="text-lg text-muted-foreground mb-6">
                Chaque heure qui passe sans action = <span className="font-bold text-foreground">vos chances diminuent</span>
              </p>
              <Button
                onClick={() => navigate('/order')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 py-3 sm:py-4 h-auto text-base sm:text-lg font-bold w-full sm:w-auto"
                data-testid="cta-pain"
              >
                Je refuse de perdre mes droits →
              </Button>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="my-12" />

      {/* Solution - COPYWRITING ÉMOTIONNEL */}
      <section className="py-12 bg-primary/5">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4 text-foreground">
              Et si vous aviez accès à un <span className="text-primary">Cabinet d'Avocat</span>
              <br />
              <span className="text-xl text-muted-foreground">...pour le prix d'une pizza ?</span>
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto mb-12">
            <h3 className="text-2xl font-bold mb-6 text-center">
              Notre équipe maîtrise PARFAITEMENT :
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">Le Code civil suisse (CC)</p>
                  <p className="text-sm text-muted-foreground">Articles pertinents appliqués à VOTRE cas</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">Le Code des obligations (CO)</p>
                  <p className="text-sm text-muted-foreground">Vos droits contractuels défendus</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">La LACI (Loi sur l'assurance-chômage)</p>
                  <p className="text-sm text-muted-foreground">Indemnités, sanctions, recours</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">Les procédures administratives cantonales</p>
                  <p className="text-sm text-muted-foreground">Délais, formes, voies de recours</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">La jurisprudence récente</p>
                  <p className="text-sm text-muted-foreground">Arguments qui fonctionnent VRAIMENT</p>
                </div>
              </div>
            </div>
          </div>

          {/* Prix */}
          <Card className="p-6 sm:p-8 max-w-2xl mx-auto mb-8 bg-gradient-to-br from-[hsl(var(--gold))]/20 to-[hsl(var(--gold))]/5 border-2 border-[hsl(var(--gold))]/40">
            <div className="text-center">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6">
                Combien ça coûte ?
              </h3>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Avocat classique</p>
                  <p className="text-2xl sm:text-3xl font-bold text-accent line-through">CHF 1'500-2'500</p>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-muted-foreground hidden sm:block">&rarr;</div>
                <div className="text-lg font-bold text-muted-foreground sm:hidden">&darr;</div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Notre service</p>
                  <p className="text-4xl sm:text-5xl font-bold text-primary">CHF 49</p>
                </div>
              </div>
              <p className="text-base sm:text-lg font-semibold mb-6">
                <span className="text-success">97% MOINS CHER</span> qu'un cabinet d'avocat
              </p>
              <Button
                size="lg"
                onClick={() => navigate('/order')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 sm:px-12 py-4 sm:py-6 h-auto text-base sm:text-xl font-bold w-full sm:w-auto"
                data-testid="cta-pricing"
              >
                Commencer maintenant — CHF 49
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Paiement sécurisé · Livraison 24-48h · Satisfaction garantie
              </p>
            </div>
          </Card>

          {/* Garanties */}
          <div className="grid sm:grid-cols-3 gap-6">
            <Card className="p-6 text-center">
              <Shield className="w-12 h-12 text-primary mx-auto mb-3" />
              <h4 className="font-bold mb-2">100% Confidentiel</h4>
              <p className="text-sm text-muted-foreground">
                Vos données sont chiffrées. Suppression sur demande.
              </p>
            </Card>
            <Card className="p-6 text-center">
              <CheckCircle className="w-12 h-12 text-primary mx-auto mb-3" />
              <h4 className="font-bold mb-2">Conforme droit suisse</h4>
              <p className="text-sm text-muted-foreground">
                Basé sur le Code civil et le Code des obligations.
              </p>
            </Card>
            <Card className="p-6 text-center">
              <Clock className="w-12 h-12 text-primary mx-auto mb-3" />
              <h4 className="font-bold mb-2">Livraison rapide</h4>
              <p className="text-sm text-muted-foreground">
                24-48h. Option express disponible (24h).
              </p>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="my-12" />

      {/* CTA Final */}
      <section className="py-16 bg-background">
        <div className="container-narrow text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ne laissez plus les administrations vous intimider
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Vous avez reçu une décision injuste ? Vous ne savez pas comment répondre ? 
            <span className="font-bold text-foreground block mt-2">Nous nous occupons de TOUT.</span>
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/order')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 sm:px-12 py-4 sm:py-6 h-auto text-base sm:text-xl font-bold w-full sm:w-auto"
            data-testid="cta-final"
          >
            Protéger mes droits maintenant — CHF 49
          </Button>
          <p className="text-xs text-muted-foreground mt-4">
            ✓ Sans engagement · ✓ Paiement sécurisé · ✓ Support client
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 bg-white">
        <div className="container-narrow">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-3xl mx-auto mb-6">
            <p className="text-xs text-amber-900 text-center">
              <strong>Disclaimer légal :</strong> Ce service propose une assistance à la rédaction uniquement. 
              Il ne constitue en aucun cas un conseil juridique. Les courriers générés ne sont pas révisés par des avocats. 
              Pour toute question juridique, veuillez consulter un professionnel du droit.
            </p>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            © 2024 Assistance Rédactionnelle | Suisse Romande
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
